import React, {
  memo, useCallback, useEffect, useState,
} from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { IconButton, TextField } from '@picsio/ui';
import { Close } from '@picsio/icons';

import { productFieldInputProps } from '../../constants';
import ReadOnlyFieldTooltip from '../../helpers/ReadOnlyFieldTooltip';
import { useLocalFieldValue, useEnterToBlur } from '../../hooks';
import parentStyles from '../../styles.module.scss';
import styles from './AggregatedScalarField.module.scss';

const AggregatedScalarField = ({
  label,
  value,
  placeholder,
  readOnly,
  tooltip,
  onChange,
  onClear,
  showClearButton,
  multiline,
  nested,
}) => {
  const stringValue = String(value ?? '');
  const { localValue, setLocalValue } = useLocalFieldValue(stringValue);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLocalValue(stringValue);
    setError(null);
  }, [stringValue, setLocalValue]);

  const revert = useCallback(() => {
    setLocalValue(stringValue);
    setError(null);
  }, [setLocalValue, stringValue]);

  const handleChange = useCallback((event) => {
    if (readOnly) return;
    setLocalValue(event.target.value);
    if (error) setError(null);
  }, [error, readOnly, setLocalValue]);

  const handleBlur = useCallback(() => {
    if (readOnly) return;

    const nextValue = String(localValue ?? '').trim();
    if (nextValue !== String(localValue ?? '')) setLocalValue(nextValue);
    if (nextValue !== stringValue) onChange?.(nextValue, revert);
  }, [localValue, onChange, readOnly, revert, setLocalValue, stringValue]);

  const handleClear = useCallback(() => {
    onClear?.(revert);
  }, [onClear, revert]);

  const handleKeyPress = useEnterToBlur();

  const displayValue = placeholder && !localValue ? '' : localValue;

  return (
    <ReadOnlyFieldTooltip tooltip={tooltip}>
      <div className={cn(nested ? styles.nestedRow : parentStyles.row)}>
        <div className={styles.fieldRow}>
          <TextField
            value={displayValue}
            disabled={readOnly}
            label={label}
            placeholder={placeholder}
            error={Boolean(error)}
            helperText={error}
            multiline={multiline}
            inputProps={productFieldInputProps}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
          />
          <If condition={showClearButton && onClear}>
            <IconButton size="lg" onClick={handleClear}>
              <Close />
            </IconButton>
          </If>
        </div>
      </div>
    </ReadOnlyFieldTooltip>
  );
};

AggregatedScalarField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  tooltip: PropTypes.string,
  showClearButton: PropTypes.bool,
  multiline: PropTypes.bool,
  nested: PropTypes.bool,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
};

AggregatedScalarField.defaultProps = {
  label: null,
  value: '',
  placeholder: '',
  readOnly: false,
  tooltip: null,
  showClearButton: false,
  multiline: false,
  nested: false,
  onChange: null,
  onClear: null,
};

export default memo(AggregatedScalarField);
